/**
 * Trend Indicator Component
 *
 * Displays a trend arrow with percentage change value.
 * Used in KPI widgets, stat cards, and summary widgets.
 *
 * @author BizClick WMS
 * @version 1.0.0
 */

import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { getTrendColor, TrendDirection } from '../../theme/dashboardTheme';

interface TrendIndicatorProps {
  /** Direction of the trend */
  trend: TrendDirection;
  /** Numeric change value (e.g., 5.2 for +5.2%) */
  value?: number;
  /** Unit to display after value (default: '%') */
  unit?: string;
  /** Whether "up" is positive (default: true). Set false for metrics like error rate. */
  upIsGood?: boolean;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Show the label text (default: true) */
  showLabel?: boolean;
}

const SIZE_CONFIG = {
  sm: { icon: 'w-3 h-3', text: 'text-xs' },
  md: { icon: 'w-4 h-4', text: 'text-sm' },
  lg: { icon: 'w-5 h-5', text: 'text-base' },
};

const TrendIndicator: React.FC<TrendIndicatorProps> = ({
  trend,
  value,
  unit = '%',
  upIsGood = true,
  size = 'sm',
  showLabel = true,
}) => {
  const colorClass = getTrendColor(trend, upIsGood);
  const { icon: iconSize, text: textSize } = SIZE_CONFIG[size];

  const TrendIcon = trend === 'up'
    ? TrendingUp
    : trend === 'down'
      ? TrendingDown
      : Minus;

  return (
    <div className={`inline-flex items-center gap-1 ${colorClass}`}>
      <TrendIcon className={iconSize} />
      {showLabel && value !== undefined && (
        <span className={`${textSize} font-medium`}>
          {trend === 'up' ? '+' : trend === 'down' ? '-' : ''}
          {Math.abs(value).toFixed(1)}{unit}
        </span>
      )}
    </div>
  );
};

export default TrendIndicator;
